import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

const ZONE = 56;      // szerokość strefy przy krawędzi (px), w której kursor przewija tabelę
const MAX_SPEED = 14; // px na klatkę przy samej krawędzi

/**
 * Poziome przewijanie szerokich tabel: najechanie kursorem na krawędź przewija
 * zawartość (szybciej im bliżej krawędzi), a cień przy krawędzi pokazuje, że są
 * dalsze kolumny. `stickyLast`: ostatnia kolumna jest przyklejona do prawej (np.
 * przełącznik „Droid”), więc prawa strefa i cień zaczynają się przed nią.
 */
export default function EdgeScroll({ children, stickyLast = false, className = '' }) {
  const ref = useRef(null);
  const dirRef = useRef(0);
  const rafRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false, stickyWidth: 0 });

  const stickyWidth = useCallback(() => {
    if (!stickyLast || !ref.current) return 0;
    const th = ref.current.querySelector('thead th:last-child');
    return th ? th.offsetWidth : 0;
  }, [stickyLast]);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    const sw = stickyWidth();
    setEdges(prev => (prev.left === left && prev.right === right && prev.stickyWidth === sw
      ? prev : { left, right, stickyWidth: sw }));
  }, [stickyWidth]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) {
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [update]);

  const step = useCallback(() => {
    const el = ref.current;
    if (!el || !dirRef.current) {
      rafRef.current = null;
      return;
    }
    el.scrollLeft += dirRef.current;
    rafRef.current = requestAnimationFrame(step);
  }, []);

  const handleMouseMove = (e) => {
    const el = ref.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const rightEdge = rect.width - stickyWidth();
    let dir = 0;
    if (x < ZONE && el.scrollLeft > 0) {
      dir = -Math.ceil(((ZONE - x) / ZONE) * MAX_SPEED);
    } else if (x > rightEdge - ZONE && x < rightEdge && el.scrollLeft + el.clientWidth < el.scrollWidth) {
      dir = Math.ceil(((x - (rightEdge - ZONE)) / ZONE) * MAX_SPEED);
    }
    dirRef.current = dir;
    if (dir && !rafRef.current) rafRef.current = requestAnimationFrame(step);
  };

  const stop = () => { dirRef.current = 0; };

  return (
    <div
      className={`edge-scroll ${edges.left ? 'edge-scroll--left' : ''} ${edges.right ? 'edge-scroll--right' : ''} ${className}`}
      style={{ position: 'relative' }}
    >
      <div ref={ref} onScroll={update} onMouseMove={handleMouseMove} onMouseLeave={stop} style={{ overflowX: 'auto' }}>
        {children}
      </div>
      {edges.left && (
        <div className="edge-scroll__hint edge-scroll__hint--left" aria-hidden="true">
          <span className="edge-scroll__caret"><CaretLeft size={12} weight="bold" /></span>
        </div>
      )}
      {edges.right && (
        <div className="edge-scroll__hint edge-scroll__hint--right" style={{ right: edges.stickyWidth }} aria-hidden="true">
          <span className="edge-scroll__caret"><CaretRight size={12} weight="bold" /></span>
        </div>
      )}
    </div>
  );
}
